import React, { useState, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import citiesData from "../../../assets/cities.json";
import { UrduInput } from "../../../Components/Shared/UrduInput";

function ModalAddCities({ isOpen, onClose, onSubmit, editCity, zones }) {
  const [cityName, setCityName] = useState("");
  const [cityName_ur, setCityNameUr] = useState("");
  const [nameError, setNameError] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [zoneError, setZoneError] = useState("");
  const [inStation, setInStation] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      setCityName("");
      setCityNameUr("");
      setNameError("");
      setZoneId("");
      setZoneError("");
      setInStation(false);
      setSearchTerm("");
      setIsDropdownOpen(false);
    } else if (editCity) {
      setCityName(editCity.name);
      setCityNameUr(editCity.cityName_ur || "");
      setNameError("");
      setZoneId(editCity.zone_id || "");
      setZoneError("");
      setInStation(editCity.is_instation || false);
      setSearchTerm(editCity.name);
    }
  }, [isOpen, editCity]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter cities based on search term (excluding "Other" option)
  const filteredCities = citiesData.filter(city =>
    city.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !city.disabled &&
    city.value !== "Other"
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    setNameError("");
    setZoneError("");

    if (!cityName) {
      setNameError("City selection is required");
      return;
    }

    if (!inStation && !zoneId) {
      setZoneError("Zone selection is required when In Station is false");
      return;
    }

    onSubmit(cityName, editCity?.id, zoneId, inStation, cityName_ur);
    setCityName("");
    setCityNameUr("");
    setNameError("");
    setZoneId("");
    setZoneError("");
    setInStation(false);
    setSearchTerm("");
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    setCityName(value);

    if (nameError && value) {
      setNameError("");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);

    if (nameError && value) {
      setNameError("");
    }
  };

  const handleCitySelect = (city) => {
    setCityName(city.value);
    setSearchTerm(city.label);
    setIsDropdownOpen(false);

    if (nameError) {
      setNameError("");
    }
  };

  const handleZoneChange = (e) => {
    const value = e.target.value;
    setZoneId(value);

    if (zoneError && value) {
      setZoneError("");
    }
  };

  const handleInStationChange = (e) => {
    const value = e.target.value === 'true';
    setInStation(value);

    if (value) {
      setZoneId("");
      setZoneError("");
    }
  };
  const isFormValid = cityName && (inStation || zoneId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg w-1/3 max-h-[80vh] relative overflow-hidden">
        <button className="absolute top-4 right-4 text-black hover:text-gray-600 transition-colors" onClick={onClose}>
          <IoClose className="w-6 h-6" />
        </button>

        <h3 className="text-xl font-bold mt-2 text-gray-800">
          {editCity ? "Edit City" : "Add City"}
        </h3>

        <form onSubmit={handleSubmit} className="h-full">
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City Name <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Search for a city..."
                className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all ${nameError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
                  }`}
                ref={searchInputRef}
              />

              {isDropdownOpen && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredCities.length > 0 ? (
                    filteredCities.map((city, index) => (
                      <div
                        key={index}
                        onClick={() => handleCitySelect(city)}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${city.value === cityName ? 'bg-blue-50 text-blue-600' : ''
                          }`}
                      >
                        {city.label}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500">No cities found</div>
                  )}
                </div>
              )}
            </div>

            {nameError && (
              <p className="text-red-500 text-sm mt-1">{nameError}</p>
            )}
            <UrduInput
              label="شہر کا نام"
              name="cityName_ur"
              value={cityName_ur}
              onChange={(e) => setCityNameUr(e.target.value)}
            />
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              In Station
            </label>
            <select
              value={inStation.toString()}
              onChange={handleInStationChange}
              className="w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all border-gray-300 focus:ring-blue-500"
            >
              <option value="false">False</option>
              <option value="true">True</option>
            </select>
          </div>

          {!inStation && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zone Name <span className="text-red-500">*</span>
              </label>
              <select
                value={zoneId}
                onChange={handleZoneChange}
                className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all ${zoneError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
                  }`}
              >
                <option value="">Select a zone</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.zone_name}</option>
                ))}
              </select>
              {zoneError && (
                <p className="text-red-500 text-sm mt-1">{zoneError}</p>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="btn btn-reset-filter justify-center"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className={`btn justify-center ${
                editCity ? "btn-edit text-white" : "btn-primary"
              } ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {editCity ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModalAddCities;
